import { useEffect, useRef, useState } from 'react';
import { ClickScrollPlugin, OverlayScrollbars } from 'overlayscrollbars';
import { useOverlayScrollbars } from 'overlayscrollbars-react';
import 'overlayscrollbars/styles/overlayscrollbars.css';
import { useLocaleContext } from '../../../../context/localeContext';
import { useAlgoliaAppliedFilter } from '../../../../hooks/useAlgoliaAppliedFilters';
import { useAlgoliaFilter } from '../../../../hooks/useAlgoliaFilter';
import { DisplayFacets } from '../DisplayFacets';
import { AppliedFilters } from '../appliedFilters/AppliedFilters';
import { Container } from './FilterMenu.styles';

export type FilterMenuProps = { facetSequence?: string[]; productCount: number };
export function FilterMenu({ facetSequence = [], productCount }: FilterMenuProps) {
  const scrollRef = useRef(null);
  const filterWrapperRef = useRef(null);
  const [isNavbarSticky, setIsNavbarSticky] = useState(false);
  const [isDisplayFacetsOverflowing, setIsDisplayFacetsOverflowing] = useState(false);

  const { ctLocale } = useLocaleContext();

  const algoliaFilter = useAlgoliaFilter(ctLocale);
  const { clearAll, currentRefinements } = useAlgoliaAppliedFilter();

  const [initialize, instance] = useOverlayScrollbars({
    options: {
      scrollbars: { autoHide: 'never', clickScroll: true, theme: 'plp-srp-theme' },
      overflow: {
        x: 'hidden',
      },
    },
  });

  // Observes any changes to navbar to push the DisplayFacets filters slightly to the bottom
  //  so the navbar doesn't cover them.
  useEffect(() => {
    const navbarElement = document.querySelector('#nav-bar-sticky-wrapper');

    const observer = new MutationObserver((mutationsList) => {
      mutationsList.forEach((mutation) => {
        const { type, attributeName } = mutation;
        const { classList } = mutation.target as HTMLDivElement;

        if (type !== 'attributes' && attributeName !== 'class') return;

        const isNavbarVisible = classList.contains('show-navbar') && classList.contains('sticky');
        setIsNavbarSticky(isNavbarVisible);
      });
    });

    if (navbarElement) {
      observer.observe(navbarElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      OverlayScrollbars.plugin(ClickScrollPlugin);
      initialize(scrollRef.current);
    }
  }, [scrollRef, initialize]);

  // Observes any change that occurs for filters container to check if their wrapper height is overflowing the overlayScrollbars div.
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      const { target } = entries[0];

      const isOverflowing = target.parentElement ? target.clientHeight > target.parentElement.clientHeight : false;
      setIsDisplayFacetsOverflowing(isOverflowing);
    });

    if (filterWrapperRef.current) {
      resizeObserver.observe(filterWrapperRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [filterWrapperRef]);

  // Update the overlayScrollbars instance whenever DisplayFacets Container starts to overflow the scrollbar div.
  // This useEffect is in place due to how overlayScrollbars and our Accordions work and change in height made via
  //  animations is not registered properly thus Y-axis scrollbar wasn't rendered properly when accordion was opened and
  //  container started to overflow.
  useEffect(() => {
    if (isDisplayFacetsOverflowing) {
      instance()?.update();
    }
  }, [isDisplayFacetsOverflowing, instance]);

  if (algoliaFilter.isFiltersEmpty) return null;

  return (
    <Container $isNavbarSticky={isNavbarSticky} role="presentation">
      <div className="overlayscrollbars-plp-srp" ref={scrollRef}>
        <div ref={filterWrapperRef}>
          <AppliedFilters priceRange={algoliaFilter.priceRange} items={currentRefinements} clearAll={clearAll} />
          <DisplayFacets productCount={productCount} facetSequence={facetSequence} algoliaFilter={algoliaFilter} />
        </div>
      </div>
    </Container>
  );
}

export default FilterMenu;
